import { useRef } from 'react'
import { Button, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileInvoice,
  faIdCard,
  faMoneyCheck,
  faCheckCircle,
  faCloudUpload,
  faTimesCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useMerchantApplication } from '../MerchantApplicationContext'
import { useSubmitMerchantApplicationMutation } from '../merchantApplicationQueries'
import EducationBlock from '../components/EducationBlock'
import StepFooter from '../components/StepFooter'

const { Title, Text } = Typography

type Props = {
  onBack: () => void
  onSubmitted: () => void
}

type DocumentType = 'taxIdDocument' | 'identificationDocument' | 'bankDocument'

const documentConfig: { type: DocumentType; label: string; description: string; icon: IconDefinition }[] = [
  {
    type: 'taxIdDocument',
    label: 'Tax ID Document',
    description: 'W-9 form or IRS EIN confirmation letter (CP 575)',
    icon: faFileInvoice,
  },
  {
    type: 'identificationDocument',
    label: 'Identification Document',
    description: 'Photo of driver\'s license, state ID, or passport',
    icon: faIdCard,
  },
  {
    type: 'bankDocument',
    label: 'Bank Document',
    description: 'Voided check, bank statement, or online banking screenshot',
    icon: faMoneyCheck,
  },
]

const DocumentsStep = ({ onBack, onSubmitted }: Props) => {
  const { state, dispatch } = useMerchantApplication()
  const { documents } = state
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const submitMutation = useSubmitMerchantApplicationMutation(
    () => {
      dispatch({ type: 'SET_SUBMITTED' })
      onSubmitted()
    },
    () => {
      dispatch({ type: 'SET_SUBMITTING', payload: false })
    }
  )

  const handleFileSelect = (docType: DocumentType, file: File | null) => {
    dispatch({ type: 'SET_DOCUMENT', documentType: docType, file })
  }

  const handleSubmit = () => {
    dispatch({ type: 'SET_SUBMITTING', payload: true })
    submitMutation.mutate({
      confirmInfo: state.confirmInfo,
      businessDetails: {
        taxId: state.businessDetails.taxId,
        organizationType: state.businessDetails.organizationType,
        averagePaymentAmount: Number(state.businessDetails.averagePaymentAmount) || 0,
        maximumPaymentAmount: Number(state.businessDetails.maximumPaymentAmount) || 0,
        monthlyPaymentTotal: Number(state.businessDetails.monthlyPaymentTotal) || 0,
      },
      owners: state.owners.map(o => ({
        firstName: o.firstName,
        lastName: o.lastName,
        email: o.email,
        phone: o.phone,
        dateOfBirth: o.dateOfBirth,
        ssn: o.ssn,
        ownershipPercentage: o.ownershipPercentage,
        address1: o.useBusinessAddress ? state.confirmInfo.address1 : o.address1,
        city: o.useBusinessAddress ? state.confirmInfo.city : o.city,
        stateCode: o.useBusinessAddress ? state.confirmInfo.stateCode : o.stateCode,
        zip: o.useBusinessAddress ? state.confirmInfo.zip : o.zip,
      })),
      bankAccount: {
        bankName: state.bankAccount.bankName,
        routingNumber: state.bankAccount.routingNumber,
        accountNumber: state.bankAccount.accountNumber,
      },
      documents: {
        taxIdDocument: documents.taxIdDocument.file ?? undefined,
        identificationDocument: documents.identificationDocument.file ?? undefined,
        bankDocument: documents.bankDocument.file ?? undefined,
      },
    })
  }

  const uploadedCount = documentConfig.filter(d => documents[d.type].file !== null).length

  return (
    <>
      <div className="merchant-app-step-header">
        <Title level={3}>Upload Documents</Title>
        <Text type="secondary">
          These documents help verify your business. They're optional right now — you can upload
          them later if you don't have them handy.
        </Text>
      </div>
      <div className="merchant-app-step-body">
        {documentConfig.map(doc => {
          const uploaded = documents[doc.type].file !== null
          return (
            <div key={doc.type}>
              <div
                className={`merchant-app-doc-card ${uploaded ? 'has-file' : ''}`}
                onClick={() => fileInputRefs.current[doc.type]?.click()}
              >
                <div className="merchant-app-doc-card-content">
                  <div
                    className="merchant-app-doc-card-icon"
                    style={{
                      background: uploaded ? '#f6ffed' : '#f5f5f5',
                      color: uploaded ? '#52c41a' : '#8c8c8c',
                    }}
                  >
                    <FontAwesomeIcon icon={uploaded ? faCheckCircle : doc.icon} />
                  </div>
                  <div className="merchant-app-doc-card-info">
                    <strong>{doc.label}</strong>
                    {uploaded ? (
                      <span style={{ color: '#52c41a' }}>{documents[doc.type].file?.name}</span>
                    ) : (
                      <span>{doc.description}</span>
                    )}
                  </div>
                  {uploaded ? (
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={e => {
                        e.stopPropagation()
                        handleFileSelect(doc.type, null)
                      }}
                    >
                      <FontAwesomeIcon icon={faTimesCircle} />
                    </Button>
                  ) : (
                    <FontAwesomeIcon icon={faCloudUpload} style={{ color: '#8c8c8c' }} />
                  )}
                </div>
                <input
                  ref={el => { fileInputRefs.current[doc.type] = el }}
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0] ?? null
                    handleFileSelect(doc.type, file)
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
          )
        })}

        <div style={{ marginTop: 8, marginBottom: 8, fontSize: 13, color: '#8c8c8c' }}>
          {uploadedCount} of 3 documents uploaded (optional)
        </div>

        <EducationBlock title="What counts as a Tax ID document?">
          <p style={{ margin: '0 0 8px 0' }}>
            The most common option is a <strong>W-9 form</strong>. If you've ever done subcontractor
            work or opened a business bank account, you've probably filled one out. You can also use
            your IRS EIN confirmation letter — this is the letter the IRS sent when you first applied
            for your EIN.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Don't have a W-9?</strong> You can fill one out in about 2 minutes.{' '}
            <a href="https://www.irs.gov/forms-pubs/about-form-w-9" target="_blank" rel="noopener noreferrer">
              Download a blank W-9 from the IRS
            </a>.
          </p>
        </EducationBlock>

        <EducationBlock title="What kind of ID do I need?">
          <p style={{ margin: '0 0 8px 0' }}>A clear photo of one of the following:</p>
          <ul style={{ margin: '0 0 8px 0', paddingLeft: 20 }}>
            <li>Driver's license (front only)</li>
            <li>State-issued ID card</li>
            <li>U.S. passport (photo page)</li>
          </ul>
          <p style={{ margin: 0 }}>
            Make sure the photo is well-lit, not blurry, and all four corners of the ID are visible.
            You can take a photo with your phone or upload an existing image.
          </p>
        </EducationBlock>

        <EducationBlock title="What counts as a bank document?">
          <p style={{ margin: '0 0 8px 0' }}>
            Any document that shows your business name, bank name, and account number. The easiest options:
          </p>
          <ul style={{ margin: '0 0 8px 0', paddingLeft: 20 }}>
            <li>A voided check from your business checking account</li>
            <li>A recent bank statement (first page is fine)</li>
            <li>A screenshot from your online banking showing account details</li>
          </ul>
          <p style={{ margin: 0 }}>
            If you connected your bank account via login in the previous step, you may not need this document.
          </p>
        </EducationBlock>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={handleSubmit}
        nextLabel="Submit Application"
        nextLoading={state.isSubmitting}
      />
    </>
  )
}

export default DocumentsStep
